/**
 * Browser-safe: DOMPurify with `window` (no jsdom). For client components only.
 */
import createDOMPurify from "dompurify";
import {
  ensureBlogHtmlHasBlocks,
  stripUndurableBlogImageSrcs,
} from "./html-body";
import { BLOG_SANITIZE_CONFIG, IFRAME_ALLOWED } from "./purify-config";

function sanitizeBlogHtmlCore(dirty: string, doc: Document): string {
  const DOMPurify = createDOMPurify(window);
  const clean = DOMPurify.sanitize(dirty, BLOG_SANITIZE_CONFIG);
  const tpl = doc.createElement("template");
  tpl.innerHTML = clean;
  const root = tpl.content;

  for (const el of root.querySelectorAll("iframe")) {
    const src = el.getAttribute("src")?.trim() ?? "";
    if (!IFRAME_ALLOWED.test(src)) {
      el.remove();
    }
  }

  for (const a of root.querySelectorAll("a[href]")) {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  }

  const out = doc.createElement("div");
  out.appendChild(root.cloneNode(true));
  return out.innerHTML;
}

/**
 * Sanitize blog HTML in the browser before `dangerouslySetInnerHTML`.
 * No-op (returns input) if `window` is undefined.
 */
export function sanitizeBlogHtmlClient(dirty: string): string {
  if (typeof window === "undefined") {
    return dirty;
  }
  const core = sanitizeBlogHtmlCore(dirty, window.document);
  return ensureBlogHtmlHasBlocks(stripUndurableBlogImageSrcs(core));
}
